import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react'
import { loadClarity } from '../analytics/clarity'

const CookieConsentContext = createContext(null)

const STORAGE_KEY = 'motorik_cookie_consent'
const VERSION = '1.0'

const isExpired = (iso) => {
  if (!iso) return true
  const saved = new Date(iso).getTime()
  if (Number.isNaN(saved)) return true
  const now = Date.now()
  const twelveMonthsMs = 365 * 24 * 60 * 60 * 1000
  return now - saved > twelveMonthsMs
}

const readConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    if (isExpired(parsed.date)) return null
    return {
      necessary: true,
      analytics: !!parsed.analytics,
      date: parsed.date,
      version: parsed.version || VERSION,
    }
  } catch {
    return null
  }
}

const writeConsent = (partial) => {
  const next = {
    necessary: true,
    analytics: !!partial.analytics,
    date: new Date().toISOString(),
    version: VERSION,
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  return next
}

export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(null)
  const [panelOpen, setPanelOpen] = useState(false)
  const [bottomOffset, setBottomOffset] = useState(0)

  useEffect(() => {
    const current = readConsent()
    setConsent(current)
  }, [])

  useEffect(() => {
    if (consent && consent.analytics === true) {
      const id = process.env.REACT_APP_CLARITY_ID
      if (id && typeof id === 'string' && id.trim().length > 0) {
        loadClarity(id.trim())
      }
    }
  }, [consent])

  const updateConsent = useCallback((partial) => {
    const next = writeConsent(partial || {})
    setConsent(next)
    setPanelOpen(false)
  }, [])

  const openConfigPanel = useCallback(() => setPanelOpen(true), [])
  const closeConfigPanel = useCallback(() => setPanelOpen(false), [])

  const isBannerVisible = useMemo(() => consent === null, [consent])

  const value = useMemo(() => ({
    consent,
    updateConsent,
    openConfigPanel,
    closeConfigPanel,
    panelOpen,
    isBannerVisible,
    bottomOffset,
    setBottomOffset,
  }), [consent, updateConsent, openConfigPanel, closeConfigPanel, panelOpen, isBannerVisible, bottomOffset])

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  )
}

export const useCookieConsent = () => useContext(CookieConsentContext)

export default CookieConsentContext

